import { useCallback, useEffect, useMemo, useState } from 'react';
import agentAdminService from '@wlb/shared/api/agent/agentAdminService';
import type {
  AdminAgentDetail,
  AgentPersona,
} from '@wlb/shared/api/agent/agentAdminTypes';
import { COUNTRIES } from '@wlb/shared/constants';
import { REGEX } from '@wlb/shared/constants/regex';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import {
  HEIGHT_SCALE_UNIT_ID,
  toHeightScaleUnitId,
} from '@wlb/shared/utils/heightUtils';
import { GENDER_ID_MAP } from '@wlb/shared/utils/userProfileMapper';
import {
  WEIGHT_LIMITS,
  WEIGHT_SCALE_UNIT_ID,
  toWeightScaleUnitId,
} from '@wlb/shared/utils/weightUtils';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import { SelectField, type SelectOption } from '../../components/ui/SelectField';
import { StateBlock } from '../../components/ui/StateBlock';
import { TextField } from '../../components/ui/TextField';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { toDateInputValue } from '../../utils/format';
import '../../components/ui/ui.css';

const MIN_PASSWORD_LENGTH = 8;

interface AgentFormDialogProps {
  /** Null opens the dialog in create mode. */
  agentId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

interface Fields {
  userName: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  mobileNo: string;
  personaId: string;
  bio: string;
  likeFrequency: string;
  commentFrequency: string;
  gender: string;
  dob: string;
  country: string;
  height: string;
  heightUnit: string;
  weight: string;
  weightUnit: string;
  startWeight: string;
  goalWeight: string;
}

const EMPTY_FIELDS: Fields = {
  userName: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  mobileNo: '',
  personaId: '',
  bio: '',
  likeFrequency: '0',
  commentFrequency: '0',
  gender: '',
  dob: '',
  country: '',
  height: '',
  heightUnit: String(HEIGHT_SCALE_UNIT_ID.CM),
  weight: '',
  weightUnit: String(WEIGHT_SCALE_UNIT_ID.LBS),
  startWeight: '',
  goalWeight: '',
};

/** Unit symbols, not prose, so they are the same in every locale. */
const HEIGHT_UNIT_OPTIONS: readonly SelectOption[] = [
  { value: String(HEIGHT_SCALE_UNIT_ID.CM), label: 'cm' },
  { value: String(HEIGHT_SCALE_UNIT_ID.FT), label: 'ft' },
];

const WEIGHT_UNIT_OPTIONS: readonly SelectOption[] = [
  {
    value: String(WEIGHT_SCALE_UNIT_ID.KG),
    label: WEIGHT_LIMITS[WEIGHT_SCALE_UNIT_ID.KG].label,
  },
  {
    value: String(WEIGHT_SCALE_UNIT_ID.LBS),
    label: WEIGHT_LIMITS[WEIGHT_SCALE_UNIT_ID.LBS].label,
  },
];

const MEASURE_FIELDS = ['height', 'weight', 'startWeight', 'goalWeight'] as const;

const isWholeCount = (value: string): boolean =>
  /^\d+$/.test(value.trim()) && Number(value) >= 0;

const isMeasure = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed === '' || (Number.isFinite(Number(trimmed)) && Number(trimmed) > 0);
};

/** `undefined` keeps a field out of the multipart body, so an untouched value
 *  is never overwritten with a blank one. */
const optionalNumber = (value: string): number | undefined =>
  value.trim() ? Number(value) : undefined;

const optionalText = (value: string): string | undefined =>
  value.trim() ? value.trim() : undefined;

const toDateInput = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : toDateInputValue(parsed);
};

const genderIdFromName = (name: string): string => {
  const id = GENDER_ID_MAP[name.trim().toLowerCase()];
  return id === undefined ? '' : String(id);
};

const countryIdFromName = (name: string): string => {
  const needle = name.trim().toLowerCase();
  if (!needle) {
    return '';
  }
  const match = COUNTRIES.find(country => country.label.toLowerCase() === needle);
  return match ? String(match.id) : '';
};

/**
 * Create or edit an AI agent. Both actions are the same multipart endpoint,
 * separated by an `Action` field, so this is one form; the password is only
 * offered on create because the API sets it once.
 */
export function AgentFormDialog({ agentId, onClose, onSaved }: AgentFormDialogProps) {
  const { t } = useTranslation('admin');
  const isEdit = agentId != null;
  const [fields, setFields] = useState<Fields>(EMPTY_FIELDS);
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async (): Promise<{ personas: AgentPersona[]; detail: AdminAgentDetail | null }> => {
      const [personas, detail] = await Promise.all([
        agentAdminService.getPersonas(),
        agentId != null ? agentAdminService.getAgent(agentId) : Promise.resolve(null),
      ]);
      return { personas, detail };
    },
    [agentId],
  );

  const { data, status, error, reload } = useAsyncResource(
    load,
    String(agentId ?? 'new'),
    t('common.errorTitle'),
  );

  const detail = data?.detail ?? null;

  useEffect(() => {
    if (!detail) {
      return;
    }
    setFields({
      userName: detail.userName,
      email: detail.email,
      password: '',
      firstName: detail.firstName,
      lastName: detail.lastName,
      mobileNo: detail.mobileNo,
      personaId: detail.personaId ? String(detail.personaId) : '',
      bio: detail.bio,
      likeFrequency: String(detail.likeFrequency),
      commentFrequency: String(detail.commentFrequency),
      gender: genderIdFromName(detail.gender),
      dob: toDateInput(detail.dateOfBirth),
      country: countryIdFromName(detail.country),
      height: detail.currentHeight != null ? String(detail.currentHeight) : '',
      heightUnit: String(toHeightScaleUnitId(detail.heightUnit)),
      weight: detail.currentWeight != null ? String(detail.currentWeight) : '',
      weightUnit: String(detail.weightUnitId ?? toWeightScaleUnitId(detail.weightUnit)),
      startWeight: detail.startWeight != null ? String(detail.startWeight) : '',
      goalWeight: detail.goalWeight != null ? String(detail.goalWeight) : '',
    });
  }, [detail]);

  const update = (key: keyof Fields, value: string) => {
    setFields(current => ({ ...current, [key]: value }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof Fields, string>> = {};
    if (!fields.userName.trim()) {
      next.userName = t('agents.form.userNameRequired');
    }
    if (!fields.email.trim()) {
      next.email = t('agents.form.emailRequired');
    } else if (!REGEX.EMAIL.test(fields.email.trim())) {
      next.email = t('agents.form.emailInvalid');
    }
    if (!isEdit && !fields.password) {
      next.password = t('agents.form.passwordRequired');
    } else if (fields.password && fields.password.length < MIN_PASSWORD_LENGTH) {
      next.password = t('agents.form.passwordTooShort');
    }
    if (!fields.personaId) {
      next.personaId = t('agents.form.personaRequired');
    }
    if (!isWholeCount(fields.likeFrequency)) {
      next.likeFrequency = t('agents.form.frequencyInvalid');
    }
    if (!isWholeCount(fields.commentFrequency)) {
      next.commentFrequency = t('agents.form.frequencyInvalid');
    }
    for (const key of MEASURE_FIELDS) {
      if (!isMeasure(fields[key])) {
        next[key] = t('agents.form.measureInvalid');
      }
    }
    if (fields.dob && fields.dob > toDateInputValue(new Date())) {
      next.dob = t('agents.form.dobInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    const height = optionalNumber(fields.height);
    const weight = optionalNumber(fields.weight);
    const startWeight = optionalNumber(fields.startWeight);
    const goalWeight = optionalNumber(fields.goalWeight);
    const sendsWeight =
      weight !== undefined || startWeight !== undefined || goalWeight !== undefined;
    try {
      await agentAdminService.saveAgent({
        action: isEdit ? 'Edit' : 'Add',
        ...(isEdit && agentId != null ? { agentId } : {}),
        userName: fields.userName.trim(),
        email: fields.email.trim(),
        ...(fields.password ? { password: fields.password } : {}),
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        mobileNo: fields.mobileNo.trim(),
        agentPersonaId: Number(fields.personaId),
        bio: fields.bio.trim(),
        likeFrequency: Number(fields.likeFrequency),
        commentFrequency: Number(fields.commentFrequency),
        gender: optionalNumber(fields.gender),
        dob: optionalText(fields.dob),
        country: optionalNumber(fields.country),
        height,
        heightScaleUnit: height !== undefined ? Number(fields.heightUnit) : undefined,
        weight,
        startWeight,
        goalWeight,
        weightScaleUnit: sendsWeight ? Number(fields.weightUnit) : undefined,
        photo,
      });
      ToastService.show({
        type: 'success',
        message: t(isEdit ? 'agents.toasts.updated' : 'agents.toasts.created'),
      });
      onSaved();
      onClose();
    } catch (cause) {
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSubmitting(false);
    }
  };

  const personaOptions = [
    { value: '', label: t('agents.form.personaPlaceholder') },
    ...(data?.personas ?? []).map(persona => ({
      value: String(persona.id),
      label: persona.name,
    })),
  ];

  const genderOptions: readonly SelectOption[] = [
    { value: '', label: t('agents.form.genderPlaceholder') },
    { value: String(GENDER_ID_MAP.male), label: t('genders.Male') },
    { value: String(GENDER_ID_MAP.female), label: t('genders.Female') },
    { value: String(GENDER_ID_MAP.other), label: t('genders.Other') },
  ];

  const countryOptions = useMemo<readonly SelectOption[]>(
    () => [
      { value: '', label: t('agents.form.countryPlaceholder') },
      ...COUNTRIES.map(country => ({
        value: String(country.id),
        label: country.label,
      })),
    ],
    [t],
  );

  return (
    <Modal
      open
      title={t(isEdit ? 'agents.form.editTitle' : 'agents.form.createTitle')}
      onClose={onClose}
      footer={
        status === 'ready' ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting
                ? t('common.saving')
                : t(isEdit ? 'agents.form.submitEdit' : 'agents.form.submitCreate')}
            </Button>
          </>
        ) : null
      }>
      {status !== 'ready' ? (
        <StateBlock status={status === 'error' ? 'error' : 'loading'} message={error} onRetry={reload} />
      ) : (
        <>
          <FormError variant="banner" message={submitError} />

          <h3 className="ui-section-title">{t('agents.form.sections.account')}</h3>
          <div className="form-grid">
            <TextField
              label={t('agents.form.userName')}
              value={fields.userName}
              error={errors.userName}
              autoComplete="off"
              onChange={event => update('userName', event.target.value)}
            />
            <TextField
              label={t('agents.form.email')}
              type="email"
              value={fields.email}
              error={errors.email}
              onChange={event => update('email', event.target.value)}
            />
            {isEdit ? null : (
              <TextField
                label={t('agents.form.password')}
                type="password"
                autoComplete="new-password"
                hint={t('agents.form.passwordHint')}
                value={fields.password}
                error={errors.password}
                onChange={event => update('password', event.target.value)}
              />
            )}
            <TextField
              label={t('agents.form.firstName')}
              value={fields.firstName}
              onChange={event => update('firstName', event.target.value)}
            />
            <TextField
              label={t('agents.form.lastName')}
              value={fields.lastName}
              onChange={event => update('lastName', event.target.value)}
            />
            <TextField
              label={t('agents.form.mobileNo')}
              type="tel"
              value={fields.mobileNo}
              onChange={event => update('mobileNo', event.target.value)}
            />
            <TextField
              label={t('agents.form.photo')}
              type="file"
              accept="image/*"
              hint={t('agents.form.photoHint')}
              onChange={event => setPhoto(event.target.files?.[0] ?? null)}
            />
          </div>

          <h3 className="ui-section-title">{t('agents.form.sections.persona')}</h3>
          <div className="form-grid">
            <SelectField
              label={t('agents.form.persona')}
              options={personaOptions}
              value={fields.personaId}
              onChange={event => update('personaId', event.target.value)}
            />
            <TextField
              label={t('agents.form.likeFrequency')}
              type="number"
              min={0}
              value={fields.likeFrequency}
              error={errors.likeFrequency}
              onChange={event => update('likeFrequency', event.target.value)}
            />
            <TextField
              label={t('agents.form.commentFrequency')}
              type="number"
              min={0}
              value={fields.commentFrequency}
              error={errors.commentFrequency}
              onChange={event => update('commentFrequency', event.target.value)}
            />
          </div>
          <div className="ui-field">
            <label className="ui-field__label" htmlFor="agent-bio">
              {t('agents.form.bio')}
            </label>
            <textarea
              id="agent-bio"
              className="ui-field__control ui-field__control--multiline"
              rows={3}
              placeholder={t('agents.form.bioPlaceholder')}
              value={fields.bio}
              onChange={event => update('bio', event.target.value)}
            />
          </div>

          <h3 className="ui-section-title">{t('agents.form.sections.profile')}</h3>
          <p className="ui-modal__note">{t('agents.form.profileHint')}</p>
          <div className="form-grid">
            <SelectField
              label={t('agents.form.gender')}
              options={genderOptions}
              value={fields.gender}
              onChange={event => update('gender', event.target.value)}
            />
            <TextField
              label={t('agents.form.dob')}
              type="date"
              value={fields.dob}
              error={errors.dob}
              onChange={event => update('dob', event.target.value)}
            />
            <SelectField
              label={t('agents.form.country')}
              options={countryOptions}
              value={fields.country}
              onChange={event => update('country', event.target.value)}
            />
            <TextField
              label={t('agents.form.height')}
              type="number"
              min={0}
              step="any"
              value={fields.height}
              error={errors.height}
              onChange={event => update('height', event.target.value)}
            />
            <SelectField
              label={t('agents.form.heightUnit')}
              options={HEIGHT_UNIT_OPTIONS}
              value={fields.heightUnit}
              onChange={event => update('heightUnit', event.target.value)}
            />
            <TextField
              label={t('agents.form.weight')}
              type="number"
              min={0}
              step="any"
              value={fields.weight}
              error={errors.weight}
              onChange={event => update('weight', event.target.value)}
            />
            <SelectField
              label={t('agents.form.weightUnit')}
              options={WEIGHT_UNIT_OPTIONS}
              value={fields.weightUnit}
              onChange={event => update('weightUnit', event.target.value)}
            />
            <TextField
              label={t('agents.form.startWeight')}
              type="number"
              min={0}
              step="any"
              value={fields.startWeight}
              error={errors.startWeight}
              onChange={event => update('startWeight', event.target.value)}
            />
            <TextField
              label={t('agents.form.goalWeight')}
              type="number"
              min={0}
              step="any"
              value={fields.goalWeight}
              error={errors.goalWeight}
              onChange={event => update('goalWeight', event.target.value)}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
